/**
 * ============================================================================
 * DEBTS v3.0 - TOKEN CONTEXT INTEGRATION
 * ============================================================================
 *
 * ✅ v3.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 *          - useToken() statt useAuth().getAccessToken()
 *          - Kein individueller Token-Fetch mehr
 *          - Verhindert Re-Render Loop & Token-Fehler
 * ✅ v2.0: PLANIVIO Rebranding + Token Fix
 * ✅ v1.0: Original Implementation
 *
 * Features:
 * - Schulden & Kredite Liste mit Progress-Bars
 * - Rate, Restlaufzeit, Abbezahlt %
 * - Kreditkarte mit Warnung
 * - Summary (Gesamt-Schulden, Monatliche Belastung)
 * - FAB Button zum Hinzufügen
 *
 * @version 3.0.0 - Token Context Integration
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v3.0: NEU!
import { useTheme } from '../../context/ThemeContext';
import Loading from '../common/Loading';
import budgetService from '../../services/budget.service';
import './Debts.css';

console.log('💳 Debts.js v3.0 loaded - TOKEN CONTEXT INTEGRATION 🔐');

function Debts({ active }) {
  // ✅ v3.0: useAuth NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated } = useAuth();

  // ✅ v3.0: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();

  const { isDark } = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [debts, setDebts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [toasts, setToasts] = useState([]);
  
  // Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    type: 'loan', // loan, mortgage, credit_card, personal
    creditor: '',
    original_amount: '',
    current_amount: '',
    interest_rate: '',
    monthly_payment: '',
    start_date: '',
    end_date: '',
    credit_limit: '',
    next_payment_date: '',
    icon: '🏦',
    notes: ''
  });

  // Debt Types
  const debtTypes = [
    { value: 'loan', label: 'Kredit', icon: '🚗' },
    { value: 'mortgage', label: 'Baufinanzierung', icon: '🏠' },
    { value: 'credit_card', label: 'Kreditkarte', icon: '💳' },
    { value: 'personal', label: 'Privatschuld', icon: '🤝' },
    { value: 'other', label: 'Sonstiges', icon: '📋' }
  ];

  // Icons for selection
  const icons = ['🏦', '🚗', '🏠', '💳', '🤝', '📋', '💰', '🎓', '📱', '🛒'];

  // Toast System
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // ============================================================================
  // ✅ v3.0: Load Data mit Token aus Context
  // ============================================================================
  const loadData = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    setLoading(true);
    try {
      console.log('🔑 Debts: Using token from TokenContext');

      const data = await budgetService.getDebts(token).catch(() => ({ debts: [] }));
      setDebts(data.debts || []);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      showToast('Fehler beim Laden: ' + error.message, 'error');
    }
    setLoading(false);
  }, [isAuthenticated, token, showToast]);

  useEffect(() => {
    if (active && isAuthenticated && token && !tokenLoading) {
      loadData();
    }
  }, [active, isAuthenticated, token, tokenLoading, loadData]);

  // ✅ v3.0: Refresh mit Token aus Context
  const refreshData = async () => {
    if (!token) return;
    try {
      const data = await budgetService.getDebts(token);
      setDebts(data.debts || []);
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    }
  };

  // ✅ v3.0: Save mit Token aus Context
  const handleSave = async () => {
    if (!formData.name || !formData.original_amount) {
      showToast('Bitte alle Pflichtfelder ausfüllen', 'error');
      return;
    }

    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }

    try {
      const data = {
        ...formData,
        original_amount: parseFloat(formData.original_amount),
        current_amount: parseFloat(formData.current_amount || formData.original_amount),
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
        monthly_payment: formData.monthly_payment ? parseFloat(formData.monthly_payment) : null,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null
      };

      if (editingItem) {
        await budgetService.updateDebt(token, editingItem.id, data);
        showToast('✅ Schuld aktualisiert', 'success');
      } else {
        await budgetService.createDebt(token, data);
        showToast('✅ Schuld erstellt', 'success');
      }

      setShowModal(false);
      setEditingItem(null);
      resetForm();
      await refreshData();
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // ✅ v3.0: Delete mit Token aus Context
  const handleDelete = async (id) => {
    if (!window.confirm('Schuld wirklich löschen?')) return;

    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }

    try {
      await budgetService.deleteDebt(token, id);
      showToast('🗑️ Gelöscht', 'success');
      await refreshData();
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // ✅ v3.0: Payment mit Token aus Context
  const handlePayment = async () => {
    if (!selectedDebt || !paymentAmount) return;

    if (!token) {
      showToast('❌ Kein Token verfügbar', 'error');
      return;
    }

    try {
      await budgetService.payDebt(token, selectedDebt.id, {
        amount: parseFloat(paymentAmount)
      });

      setShowPaymentModal(false);
      setPaymentAmount('');
      showToast(`✅ ${paymentAmount} € Zahlung erfasst`, 'success');
      await refreshData();
    } catch (error) {
      showToast('Fehler: ' + error.message, 'error');
    }
  };

  // Edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      type: item.type || 'loan',
      creditor: item.creditor || '',
      original_amount: item.original_amount?.toString() || '',
      current_amount: item.current_amount?.toString() || '',
      interest_rate: item.interest_rate?.toString() || '',
      monthly_payment: item.monthly_payment?.toString() || '',
      start_date: item.start_date?.split('T')[0] || '',
      end_date: item.end_date?.split('T')[0] || '',
      credit_limit: item.credit_limit?.toString() || '',
      next_payment_date: item.next_payment_date?.split('T')[0] || '',
      icon: item.icon || '🏦',
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'loan',
      creditor: '',
      original_amount: '',
      current_amount: '',
      interest_rate: '',
      monthly_payment: '',
      start_date: '',
      end_date: '',
      credit_limit: '',
      next_payment_date: '',
      icon: '🏦',
      notes: ''
    });
  };

  // Open Payment Modal
  const openPaymentModal = (debt) => {
    setSelectedDebt(debt);
    setPaymentAmount(debt.monthly_payment?.toString() || '');
    setShowPaymentModal(true);
  };

  // Helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount || 0));
  };

  const getProgress = (current, original) => {
    if (!original || original === 0) return 0;
    const paid = original - current;
    return Math.round((paid / original) * 100);
  };

  const getProgressColor = (debt) => {
    if (debt.type === 'credit_card') {
      // Kreditkarte: Rot wenn über 50% genutzt
      const usage = (debt.current_amount / (debt.credit_limit || debt.original_amount)) * 100;
      if (usage > 80) return '#EF4444';
      if (usage > 50) return '#F59E0B';
      return '#22C55E';
    }
    // Normale Schulden: Grün = gut (viel abbezahlt)
    const progress = getProgress(debt.current_amount, debt.original_amount);
    if (progress > 50) return '#22C55E';
    if (progress > 25) return '#F59E0B';
    return '#EF4444';
  };

  const getRemainingTime = (debt) => {
    if (!debt.monthly_payment || debt.monthly_payment === 0) return null;
    const months = Math.ceil(debt.current_amount / debt.monthly_payment);
    if (months > 12) {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return remainingMonths > 0 ? `${years} Jahre ${remainingMonths} Monate` : `${years} Jahre`;
    }
    return `${months} Monate`;
  };

  const needsWarning = (debt) => {
    if (debt.type === 'credit_card') {
      const usage = (debt.current_amount / (debt.credit_limit || debt.original_amount)) * 100;
      return usage > 30;
    }
    return false;
  };

  // Calculate Summary
  const totalDebt = debts.reduce((sum, d) => sum + parseFloat(d.current_amount || 0), 0);
  const monthlyPayments = debts.reduce((sum, d) => sum + parseFloat(d.monthly_payment || 0), 0);

  // Render Guards
  if (!active) return null;

  if (!isAuthenticated) {
    return (
      <div className={`debts-container ${isDark ? 'dark' : ''}`}>
        <div className="debts-auth-required">
          <span>🔒</span>
          <p>Bitte einloggen</p>
        </div>
      </div>
    );
  }

  // ✅ v3.0: Warte auf Token aus Context
  if (loading || tokenLoading) {
    return (
      <div className={`debts-container ${isDark ? 'dark' : ''}`}>
        <Loading icon="💳" text="Lade Schulden..." />
      </div>
    );
  }

  return (
    <div className={`debts-container ${isDark ? 'dark' : ''}`}>
      
      {/* Header */}
      <header className="debts-header">
        <div className="header-left">
          <div className="header-icon">💳</div>
          <div>
            <h1>Schulden & Kredite</h1>
            <p>{debts.length} Einträge • Gesamt: <span className="highlight">{formatCurrency(totalDebt)} €</span></p>
          </div>
        </div>
      </header>

      {/* Main Card */}
      <div className="debts-card">
        <div className="card-header">
          <h2><span>💳</span> Schulden & Kredite</h2>
        </div>

        {/* Debts List */}
        <div className="debts-list">
          {debts.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🎉</span>
              <h3>Keine Schulden</h3>
              <p>Super! Du hast aktuell keine offenen Schulden.</p>
              <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                + Kredit hinzufügen
              </button>
            </div>
          ) : (
            debts.map(debt => (
              <div key={debt.id} className="debt-item" onClick={() => handleEdit(debt)}>
                <div className="debt-main">
                  <div className="debt-icon">
                    {debt.icon || debtTypes.find(t => t.value === debt.type)?.icon || '💳'}
                  </div>
                  <div className="debt-info">
                    <div className="debt-name">{debt.name}</div>
                    <div className="debt-details">
                      {debt.creditor && <span>{debt.creditor}</span>}
                      {debt.interest_rate > 0 && <span> · {debt.interest_rate}% Zins</span>}
                      {debt.type === 'credit_card' && debt.credit_limit && (
                        <span> · Limit: {formatCurrency(debt.credit_limit)} €</span>
                      )}
                    </div>
                  </div>
                  <div className="debt-amount">
                    <div className="amount-value">{formatCurrency(debt.current_amount)} €</div>
                    {debt.type !== 'credit_card' && debt.original_amount && (
                      <div className="amount-original">von {formatCurrency(debt.original_amount)} €</div>
                    )}
                    {debt.type === 'credit_card' && (
                      <div className="amount-original">offen</div>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="debt-progress">
                  <div 
                    className="debt-progress-bar"
                    style={{ 
                      width: debt.type === 'credit_card' 
                        ? `${Math.min((debt.current_amount / (debt.credit_limit || debt.original_amount)) * 100, 100)}%`
                        : `${getProgress(debt.current_amount, debt.original_amount)}%`,
                      backgroundColor: getProgressColor(debt)
                    }}
                  />
                </div>

                {/* Meta Info */}
                <div className="debt-meta">
                  {debt.monthly_payment > 0 && (
                    <span className="meta-item">Rate: {formatCurrency(debt.monthly_payment)} €/Monat</span>
                  )}
                  {debt.type !== 'credit_card' && getRemainingTime(debt) && (
                    <span className="meta-item">Restlaufzeit: {getRemainingTime(debt)}</span>
                  )}
                  {debt.type !== 'credit_card' && debt.original_amount && (
                    <span className="meta-item">Abbezahlt: {getProgress(debt.current_amount, debt.original_amount)}%</span>
                  )}
                  {debt.type === 'credit_card' && debt.next_payment_date && (
                    <span className="meta-item">Nächste Abbuchung: {new Date(debt.next_payment_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}.</span>
                  )}
                </div>

                {/* Warning */}
                {needsWarning(debt) && (
                  <div className="debt-warning">
                    <span>⚠️</span> Ausgleich empfohlen
                  </div>
                )}

                {/* Quick Actions */}
                <div className="debt-actions" onClick={e => e.stopPropagation()}>
                  <button 
                    className="btn-pay"
                    onClick={() => openPaymentModal(debt)}
                  >
                    💰 Zahlung
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        {debts.length > 0 && (
          <div className="debts-summary">
            <div className="summary-row">
              <span>Gesamt-Schulden</span>
              <span className="summary-value danger">{formatCurrency(totalDebt)} €</span>
            </div>
            <div className="summary-row">
              <span>Monatliche Belastung</span>
              <span className="summary-value danger">{formatCurrency(monthlyPayments)} €</span>
            </div>
          </div>
        )}
      </div>


      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? '✏️ Schuld bearbeiten' : '➕ Neue Schuld'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              
              {/* Type Selection */}
              <div className="form-group">
                <label>Art der Schuld</label>
                <div className="type-grid">
                  {debtTypes.map(type => (
                    <button
                      key={type.value}
                      className={`type-btn ${formData.type === type.value ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, type: type.value, icon: type.icon }))}
                    >
                      <span>{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div className="form-group">
                <label>Bezeichnung *</label>
                <input 
                  type="text"
                  placeholder="z.B. Autokredit, Baufinanzierung..."
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              {/* Creditor */}
              <div className="form-group">
                <label>Gläubiger / Bank</label>
                <input 
                  type="text"
                  placeholder="z.B. Santander, Sparkasse..."
                  value={formData.creditor}
                  onChange={e => setFormData(prev => ({ ...prev, creditor: e.target.value }))}
                />
              </div>

              {/* Amounts Row */}
              <div className="form-row">
                <div className="form-group">
                  <label>{formData.type === 'credit_card' ? 'Aktueller Saldo' : 'Ursprungsbetrag'} *</label>
                  <div className="input-with-suffix">
                    <input 
                      type="number"
                      placeholder="0"
                      value={formData.original_amount}
                      onChange={e => setFormData(prev => ({ ...prev, original_amount: e.target.value }))}
                    />
                    <span className="suffix">€</span>
                  </div>
                </div>
                
                {formData.type !== 'credit_card' && (
                  <div className="form-group">
                    <label>Aktueller Stand</label>
                    <div className="input-with-suffix">
                      <input 
                        type="number"
                        placeholder="0"
                        value={formData.current_amount}
                        onChange={e => setFormData(prev => ({ ...prev, current_amount: e.target.value }))}
                      />
                      <span className="suffix">€</span>
                    </div>
                  </div>
                )}
                
                {formData.type === 'credit_card' && (
                  <div className="form-group">
                    <label>Kreditlimit</label>
                    <div className="input-with-suffix">
                      <input 
                        type="number"
                        placeholder="0"
                        value={formData.credit_limit}
                        onChange={e => setFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                      />
                      <span className="suffix">€</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Interest & Monthly Payment */}
              <div className="form-row">
                <div className="form-group">
                  <label>Zinssatz</label>
                  <div className="input-with-suffix">
                    <input 
                      type="number"
                      step="0.1"
                      placeholder="0.0"
                      value={formData.interest_rate}
                      onChange={e => setFormData(prev => ({ ...prev, interest_rate: e.target.value }))}
                    />
                    <span className="suffix">%</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Monatliche Rate</label>
                  <div className="input-with-suffix">
                    <input 
                      type="number"
                      placeholder="0"
                      value={formData.monthly_payment}
                      onChange={e => setFormData(prev => ({ ...prev, monthly_payment: e.target.value }))}
                    />
                    <span className="suffix">€</span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              {formData.type !== 'credit_card' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Startdatum</label>
                    <input 
                      type="date"
                      value={formData.start_date}
                      onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Enddatum</label>
                    <input 
                      type="date"
                      value={formData.end_date}
                      onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {formData.type === 'credit_card' && (
                <div className="form-group">
                  <label>Nächste Abbuchung</label>
                  <input 
                    type="date"
                    value={formData.next_payment_date}
                    onChange={e => setFormData(prev => ({ ...prev, next_payment_date: e.target.value }))}
                  />
                </div>
              )}

              {/* Icon Selection */}
              <div className="form-group">
                <label>Icon</label>
                <div className="icon-grid">
                  {icons.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-btn ${formData.icon === icon ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, icon }))}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label>Notizen</label>
                <textarea
                  placeholder="Optionale Notizen..."
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>

            </div>
            <div className="modal-footer">
              {editingItem && (
                <button 
                  className="btn-delete"
                  onClick={() => {
                    handleDelete(editingItem.id);
                    setShowModal(false);
                  }}
                >
                  🗑️ Löschen
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handleSave}>
                💾 Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedDebt && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Zahlung erfassen</h3>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="payment-preview">
                <div className="preview-icon">{selectedDebt.icon || '💳'}</div>
                <div className="preview-name">{selectedDebt.name}</div>
                <div className="preview-amount">
                  Aktuell: {formatCurrency(selectedDebt.current_amount)} €
                </div>
              </div>

              <div className="form-group">
                <label>Zahlungsbetrag</label>
                <div className="input-with-suffix">
                  <input 
                    type="number"
                    placeholder="0"
                    value={paymentAmount}
                    onChange={e => setPaymentAmount(e.target.value)}
                    autoFocus
                  />
                  <span className="suffix">€</span>
                </div>
              </div>

              {paymentAmount && (
                <div className="payment-result">
                  <div className="result-row">
                    <span>Neuer Stand:</span>
                    <span className="value">
                      {formatCurrency(Math.max(0, selectedDebt.current_amount - parseFloat(paymentAmount || 0)))} €
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handlePayment} disabled={!paymentAmount}>
                💰 Zahlung erfassen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Debts;


